import {otsBaseApi} from "../../ots-api";
import {EntityIdentifier} from "../../../model/entity";
import {Meter} from "../../../model/meter";
import {transportCategories} from "../../../model/transport-category";
import {containerLevelApi} from "../container-level/container-level-store";
import {objectChangeLogApi} from "../object-change-log/object-change-log-store";
import {ImportType} from "../../../model/import-type";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ['Meters']})

const meterApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllMeters: builder.query<Meter[], string | undefined>({
      query: (regionId) => ({
        url: `/meter/all`,
        method: 'GET',
        params: regionId ? {regionId: regionId} : undefined
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: 'Meters', id} as const)),
            {type: 'Meters', id: 'LIST'},
          ];
        } else {
          return [{type: 'Meters', id: 'LIST'}];
        }
      },
    }),
    getMetersByObject: builder.query<
      Meter[], EntityIdentifier & { regionId: string }
    >({
      query: (arg) => ({
        url: `/meter/all`,
        method: 'GET',
        params: {objectId: arg.id, objectType: arg.type, regionId: arg.regionId},
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: 'Meters', id} as const)),
            {type: 'Meters', id: 'LIST'},
          ];
        } else {
          return [{type: 'Meters', id: 'LIST'}];
        }
      },
    }),
    getMeter: builder.query<Meter, string>({
      query: (id) => ({
        url: `/meter/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{type: 'Meters', id}],
    }),
    getMeterImportTypes: builder.query<ImportType[], void>({
      query: () => ({
        url: `/meter/import-types`,
        method: "GET",
      }),
    }),
    createMeter: builder.mutation<Meter, Partial<Meter>>({
      query: (meter) => ({
        url: '/meter',
        method: 'POST',
        body: meter,
      }),
      invalidatesTags: [{type: 'Meters', id: 'LIST'}],
    }),
    updateMeter: builder.mutation<Meter, Partial<Meter>>({
      query: (meter) => ({
        url: '/meter',
        method: 'PUT',
        body: meter,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Meters', id}],
    }),
    deleteMeter: builder.mutation<void, Partial<Meter>>({
      query: (meter) => ({
        url: '/meter',
        method: 'DELETE',
        body: meter,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Meters', id}],
    }),
    updateMeterDnu: builder.mutation<void, { meterId: string, regionId: string, dnu: boolean }>({
      query: ({meterId, dnu}) => ({
        url: `/meter/${meterId}/dnu`,
        method: 'PUT',
        body: {
          dnu: dnu
        },
      }),
      async onQueryStarted({meterId, regionId, dnu, ...patch}, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled
          for (const category of transportCategories) {
            dispatch(
              containerLevelApi.util.updateQueryData('getAllContainerStatuses', {
                regionId: regionId,
                category: category.code
              }, (draft) => {
                for (const status of draft) {
                  if (status.containerMeters) {
                    const meter = status.containerMeters.find(m => m.id === meterId)
                    if (meter) {
                      meter.doNotUse = dnu
                    }
                  }
                }
              })
            )
          }

          dispatch(
            objectChangeLogApi.util.invalidateTags([
              {type: 'ObjectObjectChangeLogs', id: meterId}
            ])
          )
        } catch (error) {
          console.debug(error)
        }
      },
      invalidatesTags: (_result, _error, {meterId}, _meta) => {
        return [
          {type: 'Meters', id: meterId},
          {type: 'Meters', id: 'LIST'},
        ]
      },
    }),
    importMeters: builder.mutation<void, { importType: ImportType, file: File }>({
      query: ({importType, file}) => {
        const formData = new FormData()
        formData.append("type", importType.code)
        formData.append("file", file)
        return ({
          url: `/meter/import`,
          method: 'POST',
          body: formData,
        })
      },
      invalidatesTags: (_result, _error, _arg, _meta) => {
        return [
          {type: 'Meters'},
          {type: 'Meters', id: 'LIST'}
        ]
      },
    })
  }),
})

export const {
  useGetAllMetersQuery,
  useGetMetersByObjectQuery,
  useGetMeterQuery,
  useGetMeterImportTypesQuery,
  useCreateMeterMutation,
  useUpdateMeterMutation,
  useDeleteMeterMutation,
  useUpdateMeterDnuMutation,
  useImportMetersMutation,
} = meterApi;
